/**
 * Keycloak OIDC Authorization Code + PKCE (S256) for cm-ui-console.
 */

import { getAuthConfig, isOidcConfigured, oidcBase } from './config'
import { clearAuth, writeAuth } from './session'

const KEY_ID_TOKEN = 'cm.id_token'
const KEY_REFRESH = 'cm.refresh_token'
const KEY_VERIFIER = 'cm.pkce_verifier'
const KEY_STATE = 'cm.pkce_state'
const KEY_NEXT = 'cm.auth_next'
/** One-shot: staff path miss after login falls down instead of logout. */
const KEY_POST_LOGIN_FALLDOWN = 'cm.staff_post_login_falldown'

function b64url(bytes: Uint8Array): string {
  let s = ''
  for (const b of bytes) s += String.fromCharCode(b)
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function rand(n: number): string {
  const a = new Uint8Array(n)
  crypto.getRandomValues(a)
  return b64url(a)
}

async function challenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))
  return b64url(new Uint8Array(digest))
}

function redirectUri(): string {
  return `${location.origin}/callback`
}

export function isSafeAppPath(path: string): boolean {
  if (!path.startsWith('/')) return false
  if (path.startsWith('//')) return false
  if (path.includes('\\')) return false
  try {
    const decoded = decodeURIComponent(path)
    if (decoded.startsWith('//') || decoded.includes('://')) return false
    if (decoded.includes('\\')) return false
  } catch {
    return false
  }
  return true
}

export function rememberNext(path: string): void {
  if (!path || path === '/login' || path === '/callback') return
  if (!isSafeAppPath(path)) return
  sessionStorage.setItem(KEY_NEXT, path)
}

export function consumeNext(fallback = '/home'): string {
  const next = sessionStorage.getItem(KEY_NEXT) ?? fallback
  sessionStorage.removeItem(KEY_NEXT)
  return isSafeAppPath(next) ? next : fallback
}

/** Arm one-shot post-login falldown (call on successful OIDC callback). */
export function markPostLoginFalldown(): void {
  sessionStorage.setItem(KEY_POST_LOGIN_FALLDOWN, '1')
}

/** True once after a fresh login; clears the flag. Mid-session denials stay logout. */
export function consumePostLoginFalldown(): boolean {
  if (sessionStorage.getItem(KEY_POST_LOGIN_FALLDOWN) !== '1') return false
  sessionStorage.removeItem(KEY_POST_LOGIN_FALLDOWN)
  return true
}

/** Drop the one-shot without using it (path already allowed after login). */
export function clearPostLoginFalldown(): void {
  sessionStorage.removeItem(KEY_POST_LOGIN_FALLDOWN)
}

export function decodePreferredUsername(token: string | null): string | null {
  if (!token) return null
  try {
    const payload = token.split('.')[1]
    if (!payload) return null
    const json = JSON.parse(
      decodeURIComponent(
        atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
          .split('')
          .map((c) => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
          .join(''),
      ),
    ) as Record<string, unknown>
    if (typeof json.preferred_username === 'string') return json.preferred_username
    if (typeof json.email === 'string') return json.email
    return null
  } catch {
    return null
  }
}

export async function beginOidcLogin(loginHint?: string): Promise<void> {
  const cfg = getAuthConfig()
  if (!isOidcConfigured(cfg)) {
    throw new Error('Sign-in is not configured. Set VITE_KEYCLOAK_URL to enable Keycloak.')
  }
  callbackInFlight = null
  const verifier = rand(48)
  const state = rand(16)
  sessionStorage.setItem(KEY_VERIFIER, verifier)
  sessionStorage.setItem(KEY_STATE, state)
  const params = new URLSearchParams({
    client_id: cfg.clientId,
    response_type: 'code',
    scope: 'openid profile email',
    redirect_uri: redirectUri(),
    state,
    code_challenge: await challenge(verifier),
    code_challenge_method: 'S256',
    // Force credentials / account picker — SSO after a denied CM login would
    // otherwise re-issue a token for the same wrong-level user.
    prompt: 'login',
  })
  if (loginHint) params.set('login_hint', loginHint.trim())
  location.assign(`${oidcBase(cfg)}/auth?${params}`)
}

export type CallbackResult =
  | { ok: true }
  | { ok: false; reason: 'missing-code' | 'state' | 'token' | 'network' | 'not-configured' }

let callbackInFlight: Promise<CallbackResult> | null = null

export function handleOidcCallback(): Promise<CallbackResult> {
  if (!callbackInFlight) callbackInFlight = runOidcCallback()
  return callbackInFlight
}

/** Test helper — clears the single-flight latch between cases. */
export function resetOidcCallbackLatch(): void {
  callbackInFlight = null
}

async function runOidcCallback(): Promise<CallbackResult> {
  const cfg = getAuthConfig()
  if (!isOidcConfigured(cfg)) return { ok: false, reason: 'not-configured' }

  const url = new URL(location.href)
  if (url.searchParams.get('error')) return { ok: false, reason: 'token' }

  const code = url.searchParams.get('code')
  const state = url.searchParams.get('state')
  if (!code) return { ok: false, reason: 'missing-code' }
  if (state !== sessionStorage.getItem(KEY_STATE)) return { ok: false, reason: 'state' }

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: cfg.clientId,
    code,
    redirect_uri: redirectUri(),
    code_verifier: sessionStorage.getItem(KEY_VERIFIER) ?? '',
  })

  let response: Response
  try {
    response = await fetch(`${oidcBase(cfg)}/token`, {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body,
    })
  } catch {
    return { ok: false, reason: 'network' }
  }

  if (!response.ok) return { ok: false, reason: 'token' }

  const json = (await response.json()) as {
    access_token?: string
    id_token?: string
    refresh_token?: string
  }
  if (!json.access_token) return { ok: false, reason: 'token' }

  writeAuth({ mode: 'bearer', accessToken: json.access_token })
  if (json.id_token) sessionStorage.setItem(KEY_ID_TOKEN, json.id_token)
  if (json.refresh_token) sessionStorage.setItem(KEY_REFRESH, json.refresh_token)
  sessionStorage.removeItem(KEY_VERIFIER)
  sessionStorage.removeItem(KEY_STATE)
  markPostLoginFalldown()
  return { ok: true }
}

export type LogoutOpts = {
  /** Shown on LoginPage after Keycloak return (e.g. staff grant deny). */
  reason?: 'unauthorised' | 'ip' | 'denied' | 'error' | 'expired'
}

export function logout(opts?: LogoutOpts): void {
  const cfg = getAuthConfig()
  const idToken = sessionStorage.getItem(KEY_ID_TOKEN)
  clearAuth()
  sessionStorage.removeItem(KEY_ID_TOKEN)
  sessionStorage.removeItem(KEY_REFRESH)
  sessionStorage.removeItem(KEY_VERIFIER)
  sessionStorage.removeItem(KEY_STATE)
  sessionStorage.removeItem(KEY_NEXT)
  sessionStorage.removeItem(KEY_POST_LOGIN_FALLDOWN)
  callbackInFlight = null

  const loginPath = opts?.reason
    ? `/login?reason=${encodeURIComponent(opts.reason)}`
    : '/login'

  if (isOidcConfigured(cfg)) {
    const params = new URLSearchParams({
      client_id: cfg.clientId,
      post_logout_redirect_uri: `${location.origin}${loginPath}`,
    })
    if (idToken) params.set('id_token_hint', idToken)
    location.assign(`${oidcBase(cfg)}/logout?${params}`)
    return
  }
  location.assign(loginPath)
}
